import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  List as ListIcon, Plus, Trash, Copy, Download, Upload, Eye, 
  Pencil, Check, X, DotsSixVertical, CaretRight, CaretDown,
  MagnifyingGlass, Gear, Lightning, Desktop, DeviceTablet, DeviceMobile,
  Globe, User, ShieldCheck, Tag, BookOpen, Feather, Trophy, Link as LinkIcon,
  Minus, Star, Info, Clock, Folder, FolderOpen, ArrowsDownUp, DotsThree
} from '@phosphor-icons/react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Predefined Navigation Locations
const NAVIGATION_LOCATIONS = [
  { id: 'header', name: 'Primary Header Navigation', icon: Desktop, description: 'Main site navigation bar', color: 'blue' },
  { id: 'header-secondary', name: 'Secondary Header', icon: ListIcon, description: 'Top utility navigation', color: 'cyan' },
  { id: 'footer', name: 'Footer Navigation', icon: Desktop, description: 'Footer links and sections', color: 'purple' },
  { id: 'mobile', name: 'Mobile Navigation', icon: DeviceMobile, description: 'Mobile menu (hamburger)', color: 'green' },
  { id: 'user-menu', name: 'User Profile Menu', icon: User, description: 'Logged-in user dropdown', color: 'yellow' },
  { id: 'admin-quick', name: 'Admin Quick Links', icon: Lightning, description: 'Admin sidebar shortcuts', color: 'red' },
  { id: 'social', name: 'Social Links', icon: Globe, description: 'Social media icons', color: 'pink' },
  { id: 'legal', name: 'Legal Links', icon: ShieldCheck, description: 'Privacy, Terms, etc.', color: 'gray' },
];

// Menu Item Types
const MENU_ITEM_TYPES = [
  { id: 'page', label: 'Page', icon: BookOpen, description: 'Link to a page' },
  { id: 'category', label: 'Category', icon: Tag, description: 'Link to category archive' },
  { id: 'author', label: 'Author', icon: Feather, description: 'Link to author profile' },
  { id: 'work', label: 'Work', icon: Star, description: 'Link to specific work' },
  { id: 'competition', label: 'Competition', icon: Trophy, description: 'Link to competition' },
  { id: 'custom', label: 'Custom URL', icon: LinkIcon, description: 'External or custom link' },
  { id: 'separator', label: 'Separator', icon: Minus, description: 'Visual divider' },
  { id: 'heading', label: 'Dropdown Heading', icon: Folder, description: 'Non-clickable label' },
  { id: 'mega-menu', label: 'Mega Menu', icon: FolderOpen, description: 'Multi-column dropdown' },
];

// Sortable Menu Item Component
function SortableMenuItem({ item, onEdit, onDelete, onToggleExpand, isExpanded, depth = 0 }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;

  return (
    <div ref={setNodeRef} style={style} className={`${depth > 0 ? 'ml-8' : ''}`}>
      <div className={`bg-card border border-border rounded-lg p-3 mb-2 hover:border-primary transition-colors ${isDragging ? 'shadow-lg' : ''}`}>
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
            <DotsSixVertical size={20} weight="bold" />
          </div>

          {/* Expand/Collapse */}
          {hasChildren && (
            <button
              onClick={() => onToggleExpand(item.id)}
              className="text-muted-foreground hover:text-foreground"
            >
              {isExpanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
            </button>
          )}

          {/* Item Icon & Label */}
          <div className="flex-1 flex items-center gap-2">
            {item.icon && <span className="text-primary">{item.icon}</span>}
            <div>
              <div className="font-medium">{item.label}</div>
              <div className="text-xs text-muted-foreground">{item.url || item.type}</div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {item.badge && (
              <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-600 rounded">{item.badge}</span>
            )}
            {!item.is_visible && (
              <span className="px-2 py-1 text-xs bg-red-500/20 text-red-600 rounded">Hidden</span>
            )}
            {item.target === '_blank' && (
              <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-600 rounded">New Tab</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(item)}
              className="p-2 hover:bg-muted rounded transition-colors"
              title="Edit"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={() => onDelete(item.id)}
              className="p-2 hover:bg-red-500/10 text-red-500 rounded transition-colors"
              title="Delete"
            >
              <Trash size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProfessionalMenuBuilder() {
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('header');
  const [menuItems, setMenuItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Form states
  const [newItem, setNewItem] = useState({
    type: 'custom',
    label: '',
    url: '',
    icon: null,
    description: '',
    target: '_self',
    is_visible: true,
    visible_to_roles: [],
    badge: '',
    css_class: '',
    parent_id: null
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    if (selectedLocation && menus[selectedLocation]) {
      setMenuItems(menus[selectedLocation] || []);
    }
  }, [selectedLocation, menus]);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/menus/items/all`, { withCredentials: true });
      
      // Group by location
      const grouped = {};
      NAVIGATION_LOCATIONS.forEach(loc => {
        grouped[loc.id] = res.data[loc.id] || [];
      });
      
      setMenus(grouped);
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMenuItems((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddItem = async () => {
    try {
      const payload = {
        ...newItem,
        location: selectedLocation,
        order: menuItems.length + 1
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/menus/items`,
        payload,
        { withCredentials: true }
      );

      toast.success('Menu item added');
      setShowAddModal(false);
      setNewItem({
        type: 'custom',
        label: '',
        url: '',
        icon: null,
        description: '',
        target: '_self',
        is_visible: true,
        visible_to_roles: [],
        badge: '',
        css_class: '',
        parent_id: null
      });
      fetchMenus();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add menu item');
    }
  };

  const handleEditItem = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/menus/items/${editingItem.menu_id}`,
        editingItem,
        { withCredentials: true }
      );

      toast.success('Menu item updated');
      setShowEditModal(false);
      setEditingItem(null);
      fetchMenus();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update menu item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/menus/items/${itemId}`,
        { withCredentials: true }
      );

      toast.success('Menu item deleted');
      fetchMenus();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete menu item');
    }
  };

  const handleExport = () => {
    const data = JSON.stringify(menus, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `menu-backup-${Date.now()}.json`;
    a.click();
    toast.success('Menu exported');
  };

  const selectedLocationConfig = NAVIGATION_LOCATIONS.find(l => l.id === selectedLocation);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading menu builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6">
      {/* Left Sidebar - Menu Locations */}
      <div className="w-80 bg-card border border-border rounded-lg p-4 overflow-y-auto">
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <ListIcon size={20} weight="fill" />
            Navigation Locations
          </h2>
          <p className="text-sm text-muted-foreground">Select a location to manage</p>
        </div>

        <div className="space-y-2">
          {NAVIGATION_LOCATIONS.map(location => {
            const Icon = location.icon;
            const items = menus[location.id] || [];
            const isSelected = selectedLocation === location.id;

            return (
              <button
                key={location.id}
                onClick={() => setSelectedLocation(location.id)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  isSelected
                    ? `bg-${location.color}-500/10 border-${location.color}-500/50`
                    : 'border-border hover:border-primary hover:bg-muted'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon size={24} weight="fill" className={`text-${location.color}-500 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{location.name}</div>
                    <div className="text-xs text-muted-foreground">{location.description}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-muted rounded">{items.length} items</span>
                      {items.length > 0 && (
                        <span className="text-xs text-green-600">● Active</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-2">
          <button
            onClick={handleExport}
            className="w-full px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors flex items-center justify-center gap-2"
          >
            <Download size={16} />
            Export All Menus
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="w-full px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors flex items-center justify-center gap-2"
          >
            <Upload size={16} />
            Import Menu
          </button>
        </div>
      </div>

      {/* Center - Menu Editor */}
      <div className="flex-1 flex flex-col bg-card border border-border rounded-lg overflow-hidden">
        {/* Editor Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {selectedLocationConfig && (
                <>
                  <selectedLocationConfig.icon size={24} weight="fill" className={`text-${selectedLocationConfig.color}-500`} />
                  <div>
                    <h2 className="text-lg font-semibold">{selectedLocationConfig.name}</h2>
                    <p className="text-sm text-muted-foreground">{selectedLocationConfig.description}</p>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus size={16} weight="bold" />
              Add Item
            </button>
          </div>

          {/* Search & Preview Toggle */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg"
              />
            </div>

            <div className="flex border border-border rounded-lg overflow-hidden">
              {[
                { id: 'desktop', icon: Desktop },
                { id: 'tablet', icon: DeviceTablet },
                { id: 'mobile', icon: DeviceMobile }
              ].map(device => (
                <button
                  key={device.id}
                  onClick={() => setPreviewDevice(device.id)}
                  className={`p-2 transition-colors ${
                    previewDevice === device.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  <device.icon size={18} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Menu Items List */}
        <div className="flex-1 overflow-y-auto p-4">
          {menuItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FolderOpen size={64} className="text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Menu Items</h3>
              <p className="text-muted-foreground mb-4">
                Start building your {selectedLocationConfig?.name.toLowerCase()} by adding items
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Plus size={20} weight="bold" />
                Add First Item
              </button>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={menuItems.map(i => i.menu_id)} strategy={verticalListSortingStrategy}>
                {menuItems
                  .filter(item => 
                    item.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.url?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(item => (
                    <SortableMenuItem
                      key={item.menu_id}
                      item={{ ...item, id: item.menu_id }}
                      onEdit={(item) => {
                        setEditingItem(item);
                        setShowEditModal(true);
                      }}
                      onDelete={handleDeleteItem}
                      onToggleExpand={(id) => {
                        setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
                      }}
                      isExpanded={expandedItems[item.menu_id]}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Right Sidebar - Live Preview */}
      <div className="w-96 bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Eye size={18} />
            Live Preview
          </h3>
          <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded capitalize">{previewDevice}</span>
        </div>

        <div className={`border border-border rounded-lg p-4 bg-background ${
          previewDevice === 'mobile' ? 'max-w-[375px]' :
          previewDevice === 'tablet' ? 'max-w-[768px]' :
          'w-full'
        }`}>
          <div className="space-y-1">
            {menuItems.filter(item => item.is_visible).map(item => (
              <div
                key={item.menu_id}
                className="px-3 py-2 hover:bg-muted rounded transition-colors cursor-pointer text-sm"
              >
                {item.label}
                {item.badge && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-500/20 text-yellow-600 rounded">
                    {item.badge}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-600 dark:text-blue-400">
              <p className="font-medium mb-1">Preview Mode</p>
              <p>Changes will reflect after saving. Drag items to reorder.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Menu Item</h3>
              <button onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Item Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Item Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {MENU_ITEM_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setNewItem({ ...newItem, type: type.id })}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          newItem.type === type.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary'
                        }`}
                      >
                        <Icon size={20} className="mb-1" />
                        <div className="text-sm font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium mb-2">Label *</label>
                <input
                  type="text"
                  value={newItem.label}
                  onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                  placeholder="Menu item label"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>

              {/* URL */}
              {newItem.type !== 'separator' && newItem.type !== 'heading' && (
                <div>
                  <label className="block text-sm font-medium mb-2">URL *</label>
                  <input
                    type="text"
                    value={newItem.url}
                    onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                    placeholder="/path or https://..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description (optional)</label>
                <input
                  type="text"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Brief description for mega menus"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>

              {/* Advanced Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Badge (optional)</label>
                  <input
                    type="text"
                    value={newItem.badge}
                    onChange={(e) => setNewItem({ ...newItem, badge: e.target.value })}
                    placeholder="New, Beta, etc."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Target</label>
                  <select
                    value={newItem.target}
                    onChange={(e) => setNewItem({ ...newItem, target: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  >
                    <option value="_self">Same Tab</option>
                    <option value="_blank">New Tab</option>
                  </select>
                </div>
              </div>

              {/* Visibility */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newItem.is_visible}
                    onChange={(e) => setNewItem({ ...newItem, is_visible: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Visible</span>
                </label>
              </div>
            </div>

            <div className="border-t border-border p-4 flex gap-3">
              <button
                onClick={handleAddItem}
                disabled={!newItem.label || (newItem.type !== 'separator' && newItem.type !== 'heading' && !newItem.url)}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Item
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit Menu Item</h3>
              <button onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Label *</label>
                <input
                  type="text"
                  value={editingItem.label}
                  onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">URL *</label>
                <input
                  type="text"
                  value={editingItem.url}
                  onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Badge</label>
                  <input
                    type="text"
                    value={editingItem.badge || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, badge: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Target</label>
                  <select
                    value={editingItem.target}
                    onChange={(e) => setEditingItem({ ...editingItem, target: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  >
                    <option value="_self">Same Tab</option>
                    <option value="_blank">New Tab</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingItem.is_visible}
                    onChange={(e) => setEditingItem({ ...editingItem, is_visible: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Visible</span>
                </label>
              </div>
            </div>

            <div className="border-t border-border p-4 flex gap-3">
              <button
                onClick={handleEditItem}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
